import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { TeachersApi } from "@/openapi/generated";
import type { SearchQueryRequest } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export interface UseTeachersGetParams {
  page?: number;
  pageSize?: number;
  lastSelectedId?: number;
  active?: boolean;
  /** When false, the query does not run (e.g. when not in admin view). */
  enabled?: boolean;
  /** When set with criteria, uses searchTeachers instead of apiTeachersScrollGet. */
  searchQueryRequest?: SearchQueryRequest | null;
}

export const useTeachersGet = ({
  page = 1,
  pageSize = 100,
  lastSelectedId,
  active,
  enabled = true,
  searchQueryRequest,
}: UseTeachersGetParams = {}) => {
  const accessToken = useAccessToken();

  const {
    data,
    error,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [QueryKeys.Teachers, page, pageSize, lastSelectedId, active, searchQueryRequest],
    enabled: !!accessToken && enabled,
    queryFn: () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(TeachersApi, accessToken);

      if (searchQueryRequest?.criteria?.length) {
        return service.searchTeachers({
          page,
          pageSize,
          lastSelectedId,
          active,
          searchQueryRequest,
        });
      }

      return service.apiTeachersScrollGet({
        page,
        pageSize,
        lastSelectedId,
        active,
      });
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data,
    error,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
  };
};
